import { KeyboardArrowDown } from '@mui/icons-material';
import {
    Autocomplete,
    Chip,
    CircularProgress,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import type { FilterOptionsState } from '@mui/material/useAutocomplete';
import React, { useRef, useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import { serviceKey } from './useCustomServiceValidation';

export const dedupeCustomServices = (values: string[]): string[] => {
    const seen = new Set<string>();
    const result: string[] = [];

    values.forEach((value) => {
        const normalized = value.trim().replace(/\s+/g, ' ');
        if (!normalized) return;

        const key = serviceKey(normalized);
        if (seen.has(key)) return;

        seen.add(key);
        result.push(normalized);
    });

    return result;
};

type ServiceAutocompleteOption = {
    value?: string;
    label: string;
    inputValue?: string;
};

const defaultServiceFilter = createFilterOptions<ServiceAutocompleteOption>();

const getServiceOptionKey = (
    option: string | ServiceAutocompleteOption,
): string => {
    if (typeof option === 'string') return serviceKey(option);
    return serviceKey(option.value || option.inputValue || option.label || '');
};

const toServiceSelected = (
    current: string[],
    options: { value: string; label: string }[],
): ServiceAutocompleteOption[] =>
    current.map((item) => {
        const match = options.find((option) => option.value === item);
        if (match) return match;
        return { inputValue: item, label: item };
    });

const toServiceValues = (
    next: Array<string | ServiceAutocompleteOption>,
): string[] =>
    dedupeCustomServices(
        next.map((option) => {
            if (typeof option === 'string') return option;
            return option.inputValue || option.value || '';
        }),
    );

export type NewServiceVerdict = {
    accepted: boolean;
    /** The label to store, which the check may have cleaned up. */
    label: string;
};

interface ServicesOfferedAutocompleteProps {
    control: Control<any>;
    name: string;
    options: { value: string; label: string }[];
    /** Predefined values selected elsewhere (e.g. service grid) — hidden from this field. */
    excludeValues?: Iterable<string>;
    disabled?: boolean;
    placeholder?: string;
    /** Gate for typed-in services. Rejected entries never become chips. */
    onValidateNewValue?: (label: string) => Promise<NewServiceVerdict>;
    validating?: boolean;
    /** Fired when the contractor types, so stale validation feedback can be cleared. */
    onInputEdited?: () => void;
}

const ServicesOfferedAutocomplete = ({
    control,
    name,
    options,
    excludeValues,
    disabled = false,
    placeholder = '',
    onValidateNewValue,
    validating = false,
    onInputEdited,
}: ServicesOfferedAutocompleteProps) => {
    const [inputValue, setInputValue] = useState('');
    const [popupOpen, setPopupOpen] = useState(false);
    const validatingRef = useRef(false);

    return (
        <Controller
            name={name}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
                const current: string[] = Array.isArray(value) ? value : [];
                const excludedSet = new Set(excludeValues ?? []);
                const hasExcluded = excludedSet.size > 0;
                const dropdownOptions = hasExcluded
                    ? options.filter((opt) => !excludedSet.has(opt.value))
                    : options;
                const displayedValues = hasExcluded
                    ? current.filter((item) => !excludedSet.has(item))
                    : current;
                const picked = displayedValues.map((item) => serviceKey(item));

                return (
                    <Autocomplete<any, true, false, true>
                        multiple
                        freeSolo
                        disableCloseOnSelect
                        filterSelectedOptions
                        disabled={disabled}
                        open={popupOpen && !validating}
                        onOpen={() => {
                            if (!validating) setPopupOpen(true);
                        }}
                        onClose={() => setPopupOpen(false)}
                        options={dropdownOptions}
                        value={toServiceSelected(displayedValues, options)}
                        inputValue={inputValue}
                        onInputChange={(_, next, reason) => {
                            setInputValue(next);
                            if (reason === 'input') onInputEdited?.();
                        }}
                        size='small'
                        fullWidth
                        popupIcon={<KeyboardArrowDown />}
                        getOptionLabel={(option: any) => {
                            if (typeof option === 'string') return option;
                            if (option?.inputValue) return option.inputValue;
                            return option?.label ?? '';
                        }}
                        renderOption={(props, option: any) => {
                            const display =
                                typeof option === 'string'
                                    ? option
                                    : option?.inputValue
                                      ? `Add "${option.inputValue}"`
                                      : option?.label;
                            return <li {...props}>{display}</li>;
                        }}
                        isOptionEqualToValue={(option: any, selected: any) => {
                            const optionKey = getServiceOptionKey(option);
                            const selectedKey = getServiceOptionKey(selected);
                            return !!optionKey && optionKey === selectedKey;
                        }}
                        filterOptions={(
                            opts: ServiceAutocompleteOption[],
                            params: FilterOptionsState<ServiceAutocompleteOption>,
                        ) => {
                            const input =
                                params.inputValue
                                    ?.trim()
                                    .replace(/\s+/g, ' ') || '';
                            const byInput = defaultServiceFilter(opts, params);
                            const allSelectedKeys = new Set(
                                current.map((item) => serviceKey(item)),
                            );
                            const filtered = byInput.filter((opt) => {
                                const key = getServiceOptionKey(opt);
                                return !picked.includes(key);
                            });

                            if (input) {
                                const inputKey = serviceKey(input);
                                const alreadySelected =
                                    allSelectedKeys.has(inputKey);
                                const matchesDropdown = opts.some((opt) => {
                                    const key = getServiceOptionKey(opt);
                                    return key === inputKey;
                                });
                                const matchesPredefined = options.some(
                                    (opt) =>
                                        serviceKey(opt.value) === inputKey ||
                                        serviceKey(opt.label) === inputKey,
                                );

                                if (
                                    !alreadySelected &&
                                    !matchesDropdown &&
                                    !matchesPredefined
                                ) {
                                    filtered.push({
                                        inputValue: input,
                                        label: input,
                                    });
                                }
                            }

                            return filtered;
                        }}
                        onChange={async (_, next: any[]) => {
                            if (validatingRef.current) return;

                            const commit = (values: string[]) => {
                                if (hasExcluded) {
                                    const keptExcluded = current.filter(
                                        (item) => excludedSet.has(item),
                                    );
                                    onChange([
                                        ...keptExcluded,
                                        ...values.filter(
                                            (item) => !excludedSet.has(item),
                                        ),
                                    ]);
                                } else {
                                    onChange(values);
                                }
                            };

                            const nextValues = toServiceValues(next);
                            setInputValue('');

                            if (!onValidateNewValue) {
                                commit(nextValues);
                                return;
                            }

                            const displayedKeys = new Set(
                                displayedValues.map((item) => serviceKey(item)),
                            );

                            const approved: string[] = [];
                            validatingRef.current = true;
                            try {
                                for (const option of next) {
                                    const isFreeSolo =
                                        typeof option === 'string' ||
                                        !!option?.inputValue;
                                    const rawValue =
                                        typeof option === 'string'
                                            ? option
                                            : option.inputValue ||
                                              option.value ||
                                              option.label ||
                                              '';
                                    const value = rawValue
                                        .trim()
                                        .replace(/\s+/g, ' ');
                                    if (!value) continue;

                                    const key = serviceKey(value);

                                    // Unchanged chip already shown in this field.
                                    if (displayedKeys.has(key)) {
                                        approved.push(value);
                                        continue;
                                    }

                                    // Picked a predefined option from the dropdown.
                                    if (
                                        !isFreeSolo &&
                                        typeof option !== 'string' &&
                                        option.value
                                    ) {
                                        approved.push(option.value);
                                        continue;
                                    }

                                    // Close so the validation helper is visible.
                                    setPopupOpen(false);

                                    // Typed custom entry — validate (includes duplicate check).
                                    const verdict =
                                        await onValidateNewValue(value);
                                    if (verdict.accepted) {
                                        approved.push(verdict.label);
                                    }
                                }
                            } finally {
                                validatingRef.current = false;
                            }

                            commit(dedupeCustomServices(approved));
                        }}
                        renderTags={(tagValue: any[], getTagProps: any) =>
                            tagValue.map((option, index) => {
                                const label =
                                    typeof option === 'string'
                                        ? option
                                        : option.label ||
                                          option.inputValue ||
                                          option.value ||
                                          '';
                                const key =
                                    typeof option === 'string'
                                        ? option
                                        : option.value ||
                                          option.inputValue ||
                                          index;

                                return (
                                    <Chip
                                        {...getTagProps({
                                            index,
                                        })}
                                        key={key}
                                        label={label}
                                        size='small'
                                        sx={{
                                            fontSize: '14px',
                                            lineHeight: 1.4,
                                            height: 24,
                                            fontFamily: 'Inter',
                                        }}
                                    />
                                );
                            })
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={placeholder}
                                error={!!error}
                                helperText={
                                    validating ? (
                                        <Stack
                                            direction='row'
                                            spacing={0.75}
                                            alignItems='center'
                                        >
                                            <CircularProgress
                                                size={14}
                                                color='inherit'
                                            />
                                            <Typography
                                                variant='caption'
                                                color='text.secondary'
                                            >
                                                Validating custom service
                                            </Typography>
                                        </Stack>
                                    ) : (
                                        error?.message
                                    )
                                }
                                size='small'
                                inputProps={{
                                    ...params.inputProps,
                                    readOnly: validating,
                                }}
                                sx={{
                                    '&& input::placeholder': {
                                        opacity: 1,
                                        color: 'text.secondary',
                                    },
                                }}
                            />
                        )}
                    />
                );
            }}
        />
    );
};

export default ServicesOfferedAutocomplete;
